import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { GraduationCap, Clock, ListChecks, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { API } from "@/config";
import { useLanguage } from "@/i18n/LanguageContext";

export default function Exams() {
  const { t } = useLanguage();
  const [exams, setExams] = useState(null);

  useEffect(() => {
    axios.get(`${API}/exams`).then((r) => setExams(r.data)).catch(() => setExams([]));
  }, []);

  return (
    <div data-testid="exams-page" className="min-h-screen bg-background text-foreground">
      <Navbar />
      <section className="border-b border-border bg-muted/40">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
          <span className="inline-flex items-center gap-2 rounded-full border border-rose-500/40 bg-rose-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-rose-500">
            <GraduationCap className="h-3.5 w-3.5" />
            {t.exam.badge}
          </span>
          <h1 className="mt-4 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
            {t.exam.heading}
          </h1>
          <p className="mt-4 max-w-2xl text-base text-muted-foreground sm:text-lg">{t.exam.subheading}</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6" data-testid="exams-list-container">
        {!exams ? (
          <p className="text-sm text-muted-foreground">{t.admin.loading}</p>
        ) : exams.length === 0 ? (
          <p data-testid="no-exams-message" className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
            {t.exam.noExams}
          </p>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {exams.map((exam, i) => (
              <motion.div
                key={exam.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}
                data-testid={`exam-card-${exam.id}`}
                className="rounded-2xl border border-border bg-card p-6 shadow-sm transition-shadow hover:shadow-lg"
              >
                <h3 className="font-heading text-xl font-bold">{exam.title}</h3>
                {exam.description && (
                  <p className="mt-2 text-sm text-muted-foreground">{exam.description}</p>
                )}
                <div className="mt-4 flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <ListChecks className="h-4 w-4 text-rose-600" />
                    {exam.question_count} {t.exam.questions}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="h-4 w-4 text-rose-600" />
                    {exam.duration_minutes} {t.exam.minutes}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Target className="h-4 w-4 text-rose-600" />
                    {t.exam.passing} {exam.passing_percent}%
                  </span>
                </div>
                <Button asChild className="mt-5 gap-2" data-testid={`start-exam-${exam.id}`}>
                  <Link to={`/exams/${exam.id}`}>
                    <GraduationCap className="h-4 w-4" /> {t.exam.start}
                  </Link>
                </Button>
              </motion.div>
            ))}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
